/* ==========================================================================
   ZTweaks — behaviour
   Everything here is vanilla and self-contained so it drops straight into
   assets/theme.js in the Shopify theme. No dependencies, no build step.
   ========================================================================== */
(function () {
  'use strict';

  /* The storefront interactions are part of the product presentation and stay
     enabled consistently across browser/OS animation settings. */
  var REDUCED = false;

  /* Low-power mode. When Chrome/Edge run without GPU acceleration (disabled by
     a "tweak", blocklisted driver, remote desktop) every frame is composited
     on the CPU and a full-screen frame costs ~14ms, so any continuous
     animation pins a core and the page "freezes". WebGL reports the software
     renderer in that case, which is the one reliable signal a page can read.
     Add ?gpu=1 to the URL to force the full experience while testing. */
  var LOW_POWER = (function () {
    if (/[?&]gpu=1/.test(location.search)) return false;
    if (/[?&]gpu=0/.test(location.search)) return true;
    try {
      var c = document.createElement('canvas');
      var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      if (!gl) return true;
      var dbg = gl.getExtension('WEBGL_debug_renderer_info');
      var r = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : '';
      var lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
      return /swiftshader|llvmpipe|software|basic render|mesa offscreen/i.test(r);
    } catch (e) { return false; }
  })();
  if (LOW_POWER) document.documentElement.classList.add('low-power');

  /* In-app browsers (TikTok, Instagram, Facebook, Discord…). Their webviews ignore
     playsinline and throw any playing <video> into the app's own fullscreen
     player, which the visitor then has to fight their way out of. The inline
     script in theme.liquid flags them with html.in-app before first paint; here
     every film becomes its poster frame so nothing can ever start playing. No
     video in the theme carries a static autoplay attribute any more — they are
     all started from JS, so this swap runs before any of them could play. */
  var IN_APP = document.documentElement.classList.contains('in-app');
  (function inAppMedia() {
    if (!IN_APP) return;
    document.querySelectorAll('video').forEach(function (v) {
      var img = document.createElement('img');
      var poster = v.getAttribute('poster');
      if (poster) img.src = poster;
      img.className = v.className + ' media-image is-poster';
      img.alt = v.getAttribute('aria-label') || '';
      img.decoding = 'async';
      var box = v.parentElement;
      if (box && box.hasAttribute('data-pdp-main')) {
        box.classList.remove('is-video'); box.classList.add('is-image');
      }
      v.parentNode.replaceChild(img, v);
    });
  })();

  /* ======================================================================
     Smooth scroll — Lenis (assets/lenis.min.js, v1.3.26, vendored).
     The library is loaded as a plain <script> before this file and exposes a
     global `Lenis`, so there is no build step and it works from file:// as
     well as from the Shopify CDN.

     Lenis eases the REAL document scroll position rather than transforming a
     wrapper, which is why position: sticky (the header), the scrollbar,
     anchors and IntersectionObserver all keep behaving normally.

     Option names below are the current v1.x ones. The snippet in circulation
     is written against v0.x, which renamed three of them:
         direction        -> orientation
         gestureDirection -> gestureOrientation
         smooth           -> smoothWheel
         smoothTouch:false-> syncTouch:false   (touch stays native, as intended)
     Values are unchanged.
     ====================================================================== */
  var lenis = null;
  (function smoothScroll() {
    if (REDUCED || typeof window.Lenis !== 'function') return;

    lenis = new window.Lenis({
      // lerp (frame-rate independent) instead of a fixed-duration easing: the
      // duration mode retargets on every wheel notch and visibly steps on
      // 240-360Hz monitors; lerp stays fluid at any refresh rate.
      lerp: 0.085,
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      syncTouch: false,          // phones already have momentum; forcing it feels worse
      touchMultiplier: 2,
      infinite: false
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    window.lenis = lenis;        // handy for debugging; harmless in production
  })();

  function scrollToSection(elementId) {
    var element = document.querySelector(elementId);
    if (!element) return;
    var header = document.getElementById('site-header');
    var navbarHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 80;
    var offsetPosition = element.getBoundingClientRect().top + window.scrollY - navbarHeight;
    if (lenis) {
      lenis.scrollTo(offsetPosition, { duration: 1.2 });
    } else {
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }

  /* in-page links glide instead of jumping, and clear the floating header */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var id = a.getAttribute('href');
    var home = location.pathname === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(location.pathname);
    if (id.charAt(0) === '/' && home) {          // "/#products" on the homepage is an in-page link
      if (id.indexOf('/#') === 0) id = id.slice(1);
      else if (id === '/' || id === location.pathname) id = '#top';
    }
    if (id.charAt(0) !== '#') return;
    if (id === '#' || id.length < 2) return;
    if (id === '#top') {                       // "Home": back to the very top
      e.preventDefault();
      if (lenis) lenis.scrollTo(0, { duration: 1.2 }); else window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!document.querySelector(id)) return;
    e.preventDefault();
    scrollToSection(id);
  });

  /* ======================================================================
     Page entrance. theme.css hides each content section until html.page-ready
     (set by the inline script in theme.liquid); here they get a cascade so the
     page builds top-down instead of all at once. The hero's own pieces run
     their own sequence (sections.css), so its wrapper is skipped; on the home
     page the first section after the hero waits for that sequence to finish.
     ====================================================================== */
  (function pageEnter() {
    var main = document.getElementById('main');
    if (!main) return;
    var secs = [].filter.call(main.children, function (el) {
      return el.classList.contains('shopify-section') || el.tagName === 'SECTION' || el.classList.contains('wrap');
    });
    var hasHero = !!document.querySelector('.hero');
    var i = 0;
    secs.forEach(function (el) {
      if (el.querySelector('.hero')) return;
      var delay = (hasHero ? 420 : 60) + Math.min(i, 4) * 100;
      el.style.setProperty('--enter-delay', delay + 'ms');
      i++;
    });
  })();

  /* ======================================================================
     Reveal on enter. Everything starts hidden ONLY once JS confirms it can
     run, so the page is never blank if the script fails.
     ====================================================================== */
  (function reveal() {
    if (REDUCED || !('IntersectionObserver' in window)) return;
    var groups = ['.rule', '.collection__head', '.card', '.tier', '.panel', '.feature', '.fx__card',
                  '.quote', '.trust__item', '.faq__item', '.community', '.proof2__feet'];
    var els = document.querySelectorAll(groups.join(','));
    if (!els.length) return;
    document.body.classList.add('reveal-on');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    // stagger siblings so rows cascade rather than popping together
    var seen = new Map();
    els.forEach(function (el) {
      el.classList.add('reveal');
      var p = el.parentElement;
      var n = seen.get(p) || 0;
      seen.set(p, n + 1);
      if (n) el.style.transitionDelay = Math.min(n * 55, 280) + 'ms';
      io.observe(el);
    });
  })();

  /* ======================================================================
     Capability graphics (#proof).
     Every panel keeps moving for as long as it is on screen — the frame graph
     scrolls and stutters, the macro row sequences, the pulse loops, the debloat
     sweep repeats and the restore stack cycles. One observer per panel drives:
       .is-shown  added once, never removed — plays the one-shot draw-in
       .is-live   tracks visibility — every loop is gated on it, so nothing
                  animates off-screen or in a hidden tab
     prefers-reduced-motion skips all of it and shows the finished state.
     Every figure is a data attribute so it stays a Liquid setting.
     ====================================================================== */
  (function capabilityGraphics() {
    var caps = document.querySelectorAll('.cap[data-anim], .pv[data-anim]');
    if (!caps.length) return;

    function rnd(a, b) { return a + Math.random() * (b - a); }

    /* --- counters, re-runnable so the debloat sweep can loop -------------- */
    function animateCount(el, from, to, dur) {
      if (el._raf) cancelAnimationFrame(el._raf);
      if (REDUCED) { el.textContent = to; return; }
      var t0 = null;
      function step(ts) {
        if (t0 === null) t0 = ts;
        var k = Math.min((ts - t0) / (dur || 1100), 1);
        el.textContent = Math.round(from + (to - from) * (1 - Math.pow(1 - k, 3)));
        if (k < 1) el._raf = requestAnimationFrame(step);
      }
      el._raf = requestAnimationFrame(step);
    }
    function runCount(el) {
      if (el.dataset.counted) return;
      el.dataset.counted = '1';
      var to = parseFloat(el.getAttribute('data-count-to'));
      var from = parseFloat(el.getAttribute('data-count-from'));
      if (isNaN(to)) return;
      animateCount(el, isNaN(from) ? 0 : from, to);
    }

    /* ------------------------------------------------------------------
       01 — live frametime graph.
       This is a continuously scrolling trace, not a slideshow. A rAF loop
       translates the whole bar strip by a sub-pixel amount every frame, so
       it moves at the monitor's refresh rate the way a video does. A bar's
       height is only ever written the moment it recycles off the left edge
       and is re-appended on the right, which means nothing on screen ever
       jumps — new readings scroll in from the right instead.
       Bar height IS frame time, so a tall bar is a slow frame. The "before"
       feed spikes past the deadline at random; the "after" feed holds a
       narrow band. The fps readout is taken from the same feed.
       ------------------------------------------------------------------ */
    var GAP  = 2;        // px between bars, must match the CSS gap
    var SPAN = 34;       // readings visible across the track
    var STEP_MS = 90;    // how often a new reading arrives

    function feed(mode) {
      // roughly one frame in eight overruns its deadline on the stock feed
      if (mode === 'before') return Math.random() < 0.13 ? rnd(86, 97) : rnd(38, 62);
      return rnd(30, 36);
    }

    function frameGraph(cap) {
      var fb = cap.querySelector('.fb');
      if (!fb || fb._rows) return;
      fb._rows = [];

      fb.querySelectorAll('.fb__track').forEach(function (track) {
        var strip = track.querySelector('.fb__bars');
        if (!strip) {
          strip = document.createElement('div');
          strip.className = 'fb__bars';
          // the bars ship as plain children so the panel still reads correctly
          // with JS off; they only move into the strip once we can animate it
          [].slice.call(track.querySelectorAll('.fb-bar')).forEach(function (b) { strip.appendChild(b); });
          track.appendChild(strip);
        }
        var row = {
          track: track,
          strip: strip,
          mode:  track.getAttribute('data-graph'),
          bars:  [].slice.call(strip.children),
          read:  null,
          step:  0,
          offset: 0
        };
        var head = track.closest('.fb__row');
        var r = head && head.querySelector('.fb__fps b');
        if (r && r.hasAttribute('data-fps-flicker')) row.read = r;
        fb._rows.push(row);
      });

      /* size the bars from the real track width, and keep two spare bars so
         the right edge is never empty mid-scroll */
      function measure() {
        fb._rows.forEach(function (row) {
          var w = row.track.clientWidth;
          if (!w) return;
          var barW = Math.max(3, (w - (SPAN - 1) * GAP) / SPAN);
          row.step = barW + GAP;
          var want = SPAN + 2;
          while (row.bars.length < want) {
            var extra = row.bars[row.bars.length - 1].cloneNode(false);
            row.strip.appendChild(extra);
            row.bars.push(extra);
          }
          row.bars.forEach(function (b) { b.style.width = barW.toFixed(2) + 'px'; });
        });
      }
      measure();

      var resizeT;
      window.addEventListener('resize', function () {
        clearTimeout(resizeT);
        resizeT = setTimeout(measure, 180);
      }, { passive: true });

      function push(row) {
        var bar = row.bars.shift();                 // the one that just left
        var v = feed(row.mode);
        bar.style.height = v.toFixed(1) + '%';
        if (row.mode === 'before') {
          bar.classList.toggle('is-drop', v > 82);
          if (row.read) {
            var lo = parseInt(row.read.getAttribute('data-min'), 10) || 60;
            var hi = parseInt(row.read.getAttribute('data-max'), 10) || 90;
            // a dropped frame reads low — that is the whole point of the panel
            row.read.textContent = Math.round(v > 82 ? rnd(lo - 12, lo) : rnd(lo, hi));
          }
        }
        row.strip.appendChild(bar);                 // re-enters from the right
        row.bars.push(bar);
      }

      // let the staggered draw-in play, then hand the strip to the scroller
      setTimeout(function () {
        fb.classList.add('is-running');
        var last = 0;
        (function frame(ts) {
          fb._raf = requestAnimationFrame(frame);
          if (!last) { last = ts; return; }
          var dt = Math.min(ts - last, 120);        // a backgrounded tab must not lurch
          last = ts;
          if (!cap.classList.contains('is-live')) { last = 0; return; }

          fb._rows.forEach(function (row) {
            if (!row.step) return;
            row.offset += row.step * (dt / STEP_MS);
            while (row.offset >= row.step) { row.offset -= row.step; push(row); }
            row.strip.style.transform = 'translate3d(' + (-row.offset).toFixed(2) + 'px,0,0)';
          });
        })(0);
      }, 1000);
    }

    /* ------------------------------------------------------------------
       04 — the debloat sweep runs on a loop rather than once, so the panel
       is never sitting still: scatter, settle, refill, repeat.
       ------------------------------------------------------------------ */
    function debloatLoop(cap) {
      var pg = cap.querySelector('.pg');
      if (!pg || pg._loop) return;
      var count = pg.querySelector('.pg__count b');
      var from  = parseFloat(count && count.getAttribute('data-count-from')) || 54;
      var to    = parseFloat(count && count.getAttribute('data-count-to')) || 11;

      var label0 = pg.querySelector('[data-pg-state]');
      if (REDUCED) {
        pg.classList.add('is-purged', 'is-tidy');
        if (count) count.textContent = to;
        if (label0) { label0.textContent = label0.getAttribute('data-after'); label0.classList.add('is-good'); }
        return;
      }

      // the row label has to follow the cycle, or it claims "after debloat"
      // while 54 chips are still on screen
      var label = pg.querySelector('[data-pg-state]');
      function setLabel(after) {
        if (!label) return;
        label.textContent = label.getAttribute(after ? 'data-after' : 'data-before');
        label.classList.toggle('is-good', after);
      }

      var timers = [];
      function cycle() {
        if (!cap.classList.contains('is-live')) return;
        pg.classList.add('is-purged');
        setLabel(true);
        if (count) animateCount(count, from, to, 1000);
        timers.push(setTimeout(function () { pg.classList.add('is-tidy'); }, 950));
        timers.push(setTimeout(function () { pg.classList.remove('is-tidy'); }, 5200));
        timers.push(setTimeout(function () {
          pg.classList.remove('is-purged');
          setLabel(false);
          if (count) animateCount(count, to, from, 700);
        }, 5700));
      }
      cycle();
      pg._loop = setInterval(function () {
        timers.forEach(clearTimeout); timers = [];
        cycle();
      }, 7400);
    }

    /* ------------------------------------------------------------------
       05 — the stack restores itself on a loop as well as on click, so the
       panel demonstrates itself without waiting to be touched.
       ------------------------------------------------------------------ */
    function restoreLoop(cap) {
      var rw = cap.querySelector('.rw');
      if (!rw || rw._loop || REDUCED) return;
      function play() {
        if (!cap.classList.contains('is-live')) return;
        if (rw.classList.contains('is-restoring')) return;
        rw.classList.add('is-restoring');
        setTimeout(function () { rw.classList.remove('is-restoring'); }, 1250);
      }
      setTimeout(play, 2600);
      rw._loop = setInterval(play, 6200);
    }

    function live(cap, on) {
      cap.classList.toggle('is-live', on);
      // the frametime film only downloads when the panel is approached, and
      // it never runs off-screen
      cap.querySelectorAll('.cap__film').forEach(function (v) {
        if (LOW_POWER) { v.removeAttribute('autoplay'); return; }   // poster stands in for the film
        if (on) {
          if (!v.src && v.dataset.src) v.src = v.dataset.src;
          v.play().catch(function () {});          // blocked autoplay is not an error
        } else if (!v.paused) {
          v.pause();
        }
      });
    }

    function show(cap) {
      if (cap.classList.contains('is-shown')) return;
      cap.classList.add('is-shown');
      cap.querySelectorAll('[data-count-to]').forEach(function (el) {
        if (!el.closest('.pg')) runCount(el);      // the debloat counter loops instead
      });
      frameGraph(cap);
      debloatLoop(cap);
      restoreLoop(cap);
    }

    if (REDUCED || !('IntersectionObserver' in window)) {
      caps.forEach(function (c) {
        c.classList.add('is-shown');
        // reduced motion: the poster stays, nothing plays and nothing is
        // offered to press — a film that has to be started is not a graphic
        c.querySelectorAll('.cap__film').forEach(function (v) { v.removeAttribute('autoplay'); });
        c.querySelectorAll('[data-count-to]').forEach(runCount);
        c.querySelectorAll('[data-fps-flicker]').forEach(function (el) {
          el.textContent = (el.getAttribute('data-min') || '60') + '–' + (el.getAttribute('data-max') || '90');
        });
        debloatLoop(c);
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        // live BEFORE show — the loops check is-live on their first pass
        if (en.isIntersecting) { live(en.target, true); show(en.target); }
        else { live(en.target, false); }
      });
    }, { threshold: 0.22 });
    caps.forEach(function (c) { io.observe(c); });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) caps.forEach(function (c) { live(c, false); });
    });

    /* --- restore button: same move, on demand ---------------------------- */
    document.querySelectorAll('[data-rewind]').forEach(function (btn) {
      var rw = btn.closest('.rw');
      if (!rw) return;
      btn.addEventListener('click', function () {
        if (rw.classList.contains('is-restoring')) return;
        rw.classList.add('is-restoring');
        setTimeout(function () { rw.classList.remove('is-restoring'); }, 1250);
      });
    });
  })();

  /* Feature grid: static since v158 — nothing to drive. */

  /* ======================================================================
     Product media. Nothing downloads until it's needed: src is only attached
     when a tier scrolls into view, or when a catalogue card is hovered.
     Eight autoplaying videos on one page would be a lot to ask of a phone.
     ====================================================================== */
  (function productMedia() {
    var vids = document.querySelectorAll('video.media-video');
    if (!vids.length) return;

    function load(v) {
      if (v.dataset.loaded) return;
      v.dataset.loaded = '1';
      v.src = v.dataset.src;
    }
    vids.forEach(function (v) {
      // a missing file falls back to the placeholder art beneath it
      v.addEventListener('error', function () {
        v.classList.add('is-missing');
        var box = v.parentElement;
        if (box) box.classList.remove('has-media');
      });
    });

    // tiers: play while on screen
    var inView = document.querySelectorAll('video.media-video[data-play="view"]');
    if (LOW_POWER) {
      // software video decode: never run several previews at once — hover plays one
      inView.forEach(function (v) { v.setAttribute('data-play', 'hover'); });
      inView = [];
    }
    if (inView.length && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var v = en.target;
          if (en.isIntersecting) { load(v); v.play().catch(function () {}); }
          else if (!v.paused) { v.pause(); }
        });
      }, { threshold: 0.5 });
      inView.forEach(function (v) { io.observe(v); });
    }

    // catalogue cards: play on hover, rewind on the way out
    document.querySelectorAll('.media-video[data-play="hover"]').forEach(function (v) {
      var card = v.closest('.card') || v.parentElement;
      card.addEventListener('mouseenter', function () { load(v); v.play().catch(function () {}); });
      card.addEventListener('mouseleave', function () { v.pause(); try { v.currentTime = 0; } catch (e) {} });
      // touch devices have no hover — load it when the card comes into view
      if (window.matchMedia('(hover: none)').matches && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (es, obs) {
          es.forEach(function (e) { if (e.isIntersecting) { load(v); v.play().catch(function(){}); obs.disconnect(); } });
        }, { threshold: 0.5 }).observe(card);
      }
    });
  })();

  /* --- hero ambient motion only while the hero is on screen ---------------- */
  (function heroSleep() {
    var hero = document.querySelector('.hero');
    if (!hero || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { hero.classList.toggle('hero--asleep', !en.isIntersecting); });
    }, { threshold: 0 }).observe(hero);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) hero.classList.add('hero--asleep');
      else if (hero.getBoundingClientRect().bottom > 0) hero.classList.remove('hero--asleep');
    });
  })();

  /* --- sticky header state ------------------------------------------------ */
  var header = document.getElementById('site-header');
  if (header) {
    var hero = document.querySelector('.hero');
    var onScroll = function () {
      var y = window.scrollY;
      header.classList.toggle('is-stuck', y > 8);
      // once the hero is behind you, the bar pulls in and tightens
      var trigger = hero ? hero.offsetHeight * 0.55 : 520;
      header.classList.toggle('is-compact', y > trigger);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* --- mobile drawer ------------------------------------------------------ */
  var burger = document.getElementById('burger');
  var drawer = document.getElementById('drawer');
  var drawerClose = document.getElementById('drawer-close');

  function setDrawer(open) {
    if (!drawer || !burger) return;
    if (open) drawer.hidden = false;
    requestAnimationFrame(function () {
      drawer.classList.toggle('is-open', open);
    });
    burger.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (lenis) { open ? lenis.stop() : lenis.start(); }
    if (!open) {
      setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 320);
    }
  }

  if (burger) burger.addEventListener('click', function () { setDrawer(true); });
  if (drawerClose) drawerClose.addEventListener('click', function () { setDrawer(false); });
  if (drawer) {
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a')) setDrawer(false);
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') setDrawer(false);
  });

  /* --- collection filter chips -------------------------------------------
     Static stand-in. In the theme these become links to filtered collection
     URLs (/collections/all/macros) so the work happens server-side and each
     filter has its own shareable URL.
     ---------------------------------------------------------------------- */
  var filters = document.querySelectorAll('.filter');
  var grid = document.getElementById('product-grid');

  function applyFilter(want) {
    filters.forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.filter === want)); });
    if (!grid) return;
    grid.querySelectorAll('.card').forEach(function (card) {
      card.hidden = !(want === 'all' || card.dataset.cat === want);
    });
  }
  filters.forEach(function (btn) {
    btn.addEventListener('click', function () {
      applyFilter(btn.dataset.filter);
      if (history.replaceState) history.replaceState(null, '', btn.dataset.filter === 'all' ? location.pathname : '#' + btn.dataset.filter);
    });
  });
  // footer "Optimization / Macros / Bundles" links arrive as #optimization etc.
  function filterFromHash(scroll) {
    var h = (location.hash || '').slice(1);
    var known = [].some.call(filters, function (b) { return b.dataset.filter === h; });
    if (!known) return;
    applyFilter(h);
    if (scroll && grid) scrollToSection('#all');
  }
  if (filters.length) {
    filterFromHash(false);
    window.addEventListener('hashchange', function () { filterFromHash(true); });
  }

  /* --- whole product card is the link (client request) -------------------
     Catalogue cards, related cards and the home-page tiers all carry one real
     <a> to the product; a click anywhere else on the card follows it. Real
     links/buttons inside the card keep their own behaviour. */
  document.addEventListener('click', function (e) {
    var card = e.target.closest('.card, .tier');
    if (!card || e.target.closest('a, button, video, [data-help]')) return;
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey) { window.open(link.href, '_blank', 'noopener'); return; }
    window.location.href = link.href;
  });

  /* --- FAQ accordion ------------------------------------------------------ */
  document.querySelectorAll('.faq__q').forEach(function (q) {
    q.addEventListener('click', function () {
      var item = q.closest('.faq__item');
      var open = item.classList.toggle('is-open');
      q.setAttribute('aria-expanded', String(open));
    });
  });

  /* ======================================================================
     Product page — gallery + variants.
     Everything is server-rendered by Liquid; this only swaps the stage when a
     thumbnail is clicked (clicking the active one returns to the cover) and
     keeps the price / hidden variant id in step with the option buttons.
     ====================================================================== */
  (function productPage() {
    var main = document.querySelector('[data-pdp-main]');
    if (!main) return;
    var thumbs = document.querySelector('[data-pdp-thumbs]');

    function mediaEl(kind, src, poster) {
      if (kind === 'video' && !IN_APP) {
        var v = document.createElement('video');
        v.src = src; if (poster) v.poster = poster;
        v.muted = true; v.loop = true; v.playsInline = true;
        v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('preload', 'metadata');
        return v;
      }
      var img = document.createElement('img');
      // in-app browsers get the film's poster frame instead of the film
      img.src = (kind === 'video' && poster) ? poster : src;
      img.alt = ''; img.decoding = 'async';
      return img;
    }
    function showMain(node) {
      main.innerHTML = '';
      main.classList.add('has-media');
      main.classList.toggle('is-video', node.tagName === 'VIDEO');
      main.classList.toggle('is-image', node.tagName !== 'VIDEO');
      main.appendChild(node);
      if (node.tagName === 'VIDEO') node.play().catch(function () {});
    }
    // the server-rendered cover film has no autoplay attribute (see inAppMedia);
    // start it here, where the in-app swap has already had its say
    var cover = main.querySelector('video');
    if (cover) cover.play().catch(function () {});
    function showCover() {
      var kind = main.getAttribute('data-cover-kind');
      if (!kind) return;
      showMain(mediaEl(kind, main.getAttribute('data-cover-src'), main.getAttribute('data-cover-poster')));
    }

    if (thumbs) {
      thumbs.addEventListener('click', function (e) {
        var b = e.target.closest('.pdp__thumb');
        if (!b) return;
        var active = b.getAttribute('aria-current') === 'true';
        thumbs.querySelectorAll('.pdp__thumb').forEach(function (x) { x.removeAttribute('aria-current'); });
        if (active) { showCover(); return; }
        showMain(mediaEl(b.getAttribute('data-kind') || 'image', b.getAttribute('data-src'), b.getAttribute('data-poster')));
        b.setAttribute('aria-current', 'true');
      });
    }

    /* variants (only rendered when the product has real options) */
    var dataEl = document.querySelector('[data-pdp-variants]');
    var groups = document.querySelectorAll('[data-option-group]');
    if (!dataEl || !groups.length) return;
    var variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
    var money = function (cents) {
      var n = cents / 100;
      return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
    };
    function selected() {
      return [].map.call(groups, function (g) {
        var on = g.querySelector('.pdp__option[aria-pressed="true"]');
        return on ? on.getAttribute('data-value') : null;
      });
    }
    function sync() {
      var want = selected();
      var v = variants.filter(function (x) {
        return x.options.every(function (o, i) { return o === want[i]; });
      })[0];
      if (!v) return;
      document.querySelectorAll('[data-pdp-variant-id]').forEach(function (i) { i.value = v.id; });
      var buy = document.querySelector('[data-pdp-buy-now]');
      if (buy) buy.href = buy.href.replace(/\/cart\/\d+:1/, '/cart/' + v.id + ':1');
      document.querySelectorAll('[data-pdp-price]').forEach(function (p) { p.textContent = money(v.price); });
      var cmp = document.querySelector('[data-pdp-compare]');
      var disc = document.querySelector('[data-pdp-discount]');
      if (cmp && disc) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          cmp.textContent = money(v.compare_at_price); cmp.hidden = false;
          disc.textContent = Math.round((1 - v.price / v.compare_at_price) * 100) + '% off'; disc.hidden = false;
        } else { cmp.hidden = true; disc.hidden = true; }
      }
      document.querySelectorAll('.pdp__buy button[type="submit"]').forEach(function (b) { b.disabled = !v.available; });
      if (buy) buy.style.pointerEvents = v.available ? '' : 'none';
    }
    groups.forEach(function (group) {
      group.addEventListener('click', function (e) {
        var btn = e.target.closest('.pdp__option');
        if (!btn) return;
        group.querySelectorAll('.pdp__option').forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
        sync();
      });
    });
  })();

  /* --- animate the before/after bars into view ---------------------------- */
  var bars = document.querySelectorAll('.ba__fill');
  if (bars.length && 'IntersectionObserver' in window) {
    bars.forEach(function (b) { b.dataset.w = b.style.getPropertyValue('--w'); b.style.setProperty('--w', '0%'); });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('.ba__fill').forEach(function (b, i) {
          setTimeout(function () { b.style.setProperty('--w', b.dataset.w); }, i * 120);
        });
        io.unobserve(entry.target);
      });
    }, { threshold: 0.35 });
    document.querySelectorAll('.panel').forEach(function (p) { io.observe(p); });
  }


  /* ======================================================================
     Help widget — preset questions only. No text input, nothing is sent
     anywhere; answers are copied out of an inline <template> in the same
     order as the question buttons.
     ====================================================================== */
  (function helpWidget() {
    var root = document.querySelector('[data-help]');
    if (!root) return;
    var toggle = root.querySelector('[data-help-toggle]');
    var panel = root.querySelector('[data-help-panel]');
    var list = root.querySelector('[data-help-list]');
    var answer = root.querySelector('[data-help-answer]');
    var aTitle = root.querySelector('[data-help-a-title]');
    var aText = root.querySelector('[data-help-a-text]');
    var tpl = root.querySelector('[data-help-answers]');
    var answers = tpl ? [].slice.call(tpl.content.children).map(function (n) { return n.innerHTML; }) : [];
    var body = root.querySelector('.help__body');

    function setOpen(open) {
      root.classList.toggle('is-open', open);
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close quick questions' : 'Open quick questions');
      if (open) { showList(); var first = list.querySelector('.help__q'); if (first) first.focus({ preventScroll: true }); }
      else toggle.focus({ preventScroll: true });
    }
    function showList() { answer.hidden = true; list.hidden = false; if (body) body.scrollTop = 0; }
    function showAnswer(i, label) {
      aTitle.textContent = label;
      aText.innerHTML = answers[i] || '';
      list.hidden = true; answer.hidden = false;
      if (body) body.scrollTop = 0;
      var back = answer.querySelector('[data-help-back]'); if (back) back.focus({ preventScroll: true });
    }

    toggle.addEventListener('click', function () { setOpen(panel.hidden); });
    list.addEventListener('click', function (e) {
      var q = e.target.closest('[data-help-q]');
      if (!q) return;
      showAnswer(+q.getAttribute('data-help-q'), q.textContent.trim());
    });
    answer.addEventListener('click', function (e) { if (e.target.closest('[data-help-back]')) showList(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) setOpen(false); });
    document.addEventListener('click', function (e) { if (!panel.hidden && !root.contains(e.target)) setOpen(false); });
  })();
})();
